import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useNewLoginAPI from "../../api/loginAPI";
import useNewValidateToken from "../../authentication/ValidateToken";
import { checkQrStatusTimer, qrTimeoutMinutes } from "../../../utils/Constants";
import { BUILD_VERSION } from "../../../generated/buildVersion";
import "../../Styles/pages/onboard.scss";

const versionedImage = (path) => `${path}?v=${BUILD_VERSION}`;

function NewBarcode() {
  const { isLoading, PortalOnBoard, CheckQrStatus, deepLinkData } =
    useNewLoginAPI();

  const { checkTokenExpiry } = useNewValidateToken();

  const [qrUrl, setQrUrl] = useState("");
  const [qrTimeOut, setQrTimeOut] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentYear] = useState(new Date().getFullYear());

  const navigate = useNavigate();
  const mountedRef = useRef(false);

  const actionsRef = useRef({
    CheckQrStatus,
    PortalOnBoard,
    checkTokenExpiry,
    navigate,
  });

  actionsRef.current = {
    CheckQrStatus,
    PortalOnBoard,
    checkTokenExpiry,
    navigate,
  };

  const hasJWT = () => !!sessionStorage.getItem("Token");

  const getCode = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has("code") ? urlParams.get("code") : "";
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const code = getCode();

    if (code) {
      sessionStorage.setItem("RegCodUrl", code);
    }
  }, []);

  useEffect(() => {
    if (hasJWT()) {
      actionsRef.current
        .checkTokenExpiry()
        .then(() =>
          actionsRef.current.navigate("/overview", {
            replace: true,
          })
        )
        .catch(() =>
          actionsRef.current.PortalOnBoard(setQrTimeOut, setQrUrl, getCode())
        );

      return;
    }

    actionsRef.current.PortalOnBoard(setQrTimeOut, setQrUrl, getCode());
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (qrTimeOut && (event.keyCode === 13 || event.keyCode === 32)) {
        actionsRef.current.PortalOnBoard(setQrTimeOut, setQrUrl, getCode());
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [qrTimeOut]);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }

    let countDownDate = new Date();

    countDownDate.setMinutes(countDownDate.getMinutes() + qrTimeoutMinutes);

    countDownDate = countDownDate.getTime();

    const timerStatus = setInterval(() => {
      const now = new Date().getTime();
      const distance = countDownDate - now;

      if (distance < 0) {
        clearInterval(timerStatus);
        setQrTimeOut(true);
        return;
      }

      if (sessionStorage.getItem("SessionID") && qrUrl.length > 0) {
        actionsRef.current
          .CheckQrStatus(timerStatus, setQrTimeOut)
          .catch(() => {
            clearInterval(timerStatus);
            setQrTimeOut(true);
          });
      }
    }, checkQrStatusTimer);

    return () => {
      clearInterval(timerStatus);
    };
  }, [CheckQrStatus, qrUrl]);

  return (
    <OnboardScreen
      currentYear={currentYear}
      isLoading={isLoading}
      isMobile={isMobile}
      qrTimeOut={qrTimeOut}
      qrUrl={qrUrl}
      onMobileLogin={() => {
        window.location.href = deepLinkData;
      }}
      onReloadQr={() =>
        actionsRef.current.PortalOnBoard(setQrTimeOut, setQrUrl, getCode())
      }
    />
  );
}

const promoBanners = [
  {
    className: "promo-payroll",
    theme: "payroll",
    image: "/Images/PaymentRequestImage.png",
    logo: "/Images/Vector.png",
    title: "Payroll Solution",
    description: "Pay your team instantly into their Suyool wallets.",
    buttonText: "Learn More",
    buttonAction: () =>
      window.open(
        "https://suyool.com/payroll",
        "_blank",
        "noopener,noreferrer"
      ),
    hasButton: true,
  },
  {
    className: "promo-cash",
    theme: "inverse",
    image: "/Images/CashButlerServiceImage.png",
    logo: "/Images/BikeLogo.png",
    title: "Cash Butler Service",
    description:
      "Lebanese hand-picked drivers to pick up or deliver your cash.",
    buttonText: "Learn More",
    buttonAction: () =>
      window.open(
        "https://suyool.com/cash-pickup-delivery",
        "_blank",
        "noopener,noreferrer"
      ),
    hasButton: true,
  },
  {
    className: "promo-yool",
    theme: "navy",
    image: "/Images/YoolImage.png",
    logo: "/Images/YoolLogo.png",
    title: "YOOL AI Assistant",
    description: "Manage your banking, approvals, transfers, and more.",
    buttonText: "Learn More",
    buttonAction: () =>
      window.open("https://suyool.com", "_blank", "noopener,noreferrer"),
    hasButton: true,
  },
  {
    className: "promo-currency",
    theme: "inverse",
    image: "/Images/CurrencyExchangeImage.png",
    logo: "/Images/CurrencyExchangeLogo.png",
    title: "Currency Exchange",
    description: "Exchange between USD and LBP in seconds.",
    buttonText: "Learn More",
    buttonAction: () =>
      window.open(
        "https://suyool.com/exchange",
        "_blank",
        "noopener,noreferrer"
      ),
    hasButton: true,
  },
  {
    className: "promo-transfer",
    theme: "inverse",
    image: "/Images/InternationalTransfersImage.png",
    logo: "/Images/TransferLogo.png",
    title: "International Transfers",
    description: "Secure and reliable international transfers.",
    buttonText: "Learn More",
    buttonAction: () =>
      window.open(
        "https://suyool.com/international-transfers",
        "_blank",
        "noopener,noreferrer"
      ),
    hasButton: true,
  },
  {
    className: "promo-merchant",
    theme: "navy",
    image: "/Images/MerchantPaymentsImage.png",
    logo: "/Images/kBAL9R.tif.png",
    title: "Merchant Payments",
    description: "Accept customer payments without hassle.",
    buttonText: "Learn More",
    buttonAction: () =>
      window.open(
        "https://suyool.com/merchant-app",
        "_blank",
        "noopener,noreferrer"
      ),
    hasButton: true,
  },
  {
    className: "promo-paymentrequest",
    theme: "payroll",
    image: "/Images/PaymentRequestImage.png",
    logo: "/Images/PaymentRequestLogo.png",
    title: "Payment Request",
    description: "Send payment request links to individuals or businesses.",
    buttonText: "Learn More",
    buttonAction: () =>
      window.open("https://suyool.com", "_blank", "noopener,noreferrer"),
    hasButton: true,
  },

  {
    className: "promo-taxes",
    theme: "inverse",
    image: "/Images/Pay-taxes.webp",
    logo: "/Images/vat-icon.svg",
    title: "Pay your taxes",
    description:
      "Manage and pay your taxes from your desk using your corporate account.",
    buttonText: "Learn More",
    buttonAction: () =>
      window.open(
        "https://suyool.com/incometax",
        "_blank",
        "noopener,noreferrer"
      ),
    hasButton: false,
  },
];

const featureCards = [
  {
    title: "Payroll Solution",
    description: "Pay your team instantly into their Suyool wallets.",
    icon: "/Images/payroll-solution.svg",
    isPopular: false,
    isNew: false,
  },
  {
    title: "Cash Butler Service",
    description:
      "Lebanese hand-picked drivers to pick up or deliver your cash.",
    icon: "/Images/cash-delivery-icon.svg",
    isPopular: true,
    isNew: false,
  },
  {
    title: "Pay VAT",
    description: "Pay your VAT directly from the portal.",
    icon: "/Images/vat-icon.svg",
    isPopular: false,
    isNew: true,
  },
  {
    title: "Merchant Payments",
    description: "Accept customer payments without hassle.",
    icon: "/Images/merchant-payment-icon.svg",
    isPopular: false,
    isNew: false,
  },
  {
    title: "International Transfers",
    description: "Secure and reliable international transfers.",
    icon: "/Images/international-icon.svg",
    isPopular: false,
    isNew: false,
  },
  {
    title: "Corporate Cards",
    description: "Request as many cards as needed for your team.",
    icon: "/Images/corporate-card-icon.svg",
    isPopular: false,
    isNew: false,
  },
];

function getFeatureBadge(feature) {
  if (feature.isPopular) return "POPULAR";
  if (feature.isNew) return "NEW";
  return "";
}

function PromoBanner({ banner }) {
  return (
    <div
      className={`new-onboard__promo new-onboard__promo--${banner.theme} ${banner.className}`}
      style={{
        backgroundImage: `url(${versionedImage(banner.image)})`,
      }}
    >
      <div className="new-onboard__promo-overlay">
        <div className="new-onboard__promo-content">
          <img
            className="new-onboard__promo-logo"
            src={versionedImage(banner.logo)}
            alt={banner.title}
          />

          <div className="new-onboard__promo-text">
            <h2>{banner.title}</h2>
            <p>{banner.description}</p>
            {banner.hasButton && (
              <button
                className="new-onboard__promo-button"
                type="button"
                onClick={banner.buttonAction}
              >
                {banner.buttonText}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function OnboardScreen({
  currentYear,
  isLoading,
  isMobile,
  qrTimeOut,
  qrUrl,
  onMobileLogin,
  onReloadQr,
}) {
  const selectedBanner =
    promoBanners.find((banner) => banner.className === "promo-taxes") ||
    promoBanners[0];

  return (
    <main
      className="new-onboard"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/Images/onboard-background.png)`,
      }}
    >
      <section
        className="new-onboard__left"
        aria-label="Suyool corporate portal"
      >
        <div className="new-onboard__left-line" />

        <div className="new-onboard__content">
          <header className="new-onboard__brand">
            <img
              className="new-onboard__brand-logo"
              src="/Images/suyool-white.svg"
              alt="Suyool"
            />

            <p>Corporate Portal</p>
          </header>

          <PromoBanner banner={selectedBanner} />

          <div className="new-onboard__features" aria-label="Portal features">
            {featureCards.map((feature) => {
              const badge = getFeatureBadge(feature);

              return (
                <article
                  className="new-onboard__feature-card"
                  key={feature.title}
                >
                  {badge && (
                    <span className="new-onboard__feature-badge">{badge}</span>
                  )}

                  <span className="new-onboard__feature-icon">
                    <img src={versionedImage(feature.icon)} alt="" />
                  </span>

                  <div className="new-onboard__feature-copy">
                    <h2>{feature.title}</h2>

                    <p>{feature.description}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
      <section className="new-onboard__right" aria-label="Login">
        <div className="new-onboard__login-card">
          <img
            className="new-onboard__login-logo"
            src="/Images/suyool-logo.svg"
            alt="Suyool"
          />

          <h1>
            Log in to your <br /> corporate account{" "}
          </h1>

          <div className="new-onboard__qr-stage">
            {isMobile ? (
              <button
                className="new-onboard__mobile-login"
                type="button"
                onClick={onMobileLogin}
              >
                Login with Suyool App
              </button>
            ) : isLoading ? (
              <img
                src="/Images/skash_Rocket.gif"
                className="new-onboard__loading"
                alt="loading"
              />
            ) : (
              <div
                className="new-onboard__qr-box"
                style={{
                  backgroundImage: `url(${qrUrl})`,
                }}
              >
                {qrTimeOut && (
                  <button
                    className="new-onboard__qr-reload"
                    type="button"
                    onClick={onReloadQr}
                  >
                    <span className="new-onboard__qr-reload-inner">
                      <img src="/Images/skash_loading.png" alt="reload" />

                      <span>CLICK TO RELOAD QR CODE</span>
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
          {!isMobile && !isLoading && qrUrl && (
            <p className="new-onboard__scan-login-label">Scan to Log In</p>
          )}

          <p className="new-onboard__copyright">
            {currentYear} Copyrights © Suyool
          </p>
        </div>
      </section>
    </main>
  );
}

export default NewBarcode;
